import React, { FC, useEffect, useState } from 'react';
import { Box, Grid, Typography, useTheme } from '@material-ui/core';
import { Link, navigate, useLocation, useParams } from '@reach/router';
import Backdrop from '../../backdrop/Backdrop';
import colors from '../../../themes/main-colors';
import { Layout } from '../../pge-plus-common/Layout';
import ROUTES from '../../../routes';
import useContentMgmt from '../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { useInstallerJobDetailsQuery } from '../../../hooks/pge-plus/useInstallerJobDetailsQuery';
import {
  JobStatus,
  LineItemCost,
  LineItemProduct,
  LineItemRebate,
  QuoteDocument,
  QuoteStatus,
} from '../../../__generated__/pgeplus-types';
import QuoteCustomerInfo from '../installer-common/quote-customer-info';
import JobInstallationCostDisplay from '../installer-common/jobInstallationCostDisplay';
import useQuote from '../confirm-cost/useQuote';
import { boldText, subTextWithoutMargin } from '../installer-common/quoteCss';
import InstallerButton from '../../pge-plus-common/InstallerButton';
import { format, parse } from 'date-fns';
import InstallerPageHeader from '../../pge-plus-common/InstallerPageHeader';
import { toCurrencyString } from '../../../util/format';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import useFileUploadApi from '../../../hooks/pge-plus/useFileUploadApi';
import useUtil from '../../../hooks/pge-plus/useUtil';

interface Props {
  path?: string;
}
const ViewQuote: FC<Props> = props => {
  const { content, inlineRichText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLER_VIEW_QUOTE,
  );
  const { setErrorNotification } = useUtil();

  const theme = useTheme();
  const { jobId } = useParams();
  const { loading, data, error } = useInstallerJobDetailsQuery(jobId);
  const { wrapWithLoader } = useWrapWithLoader();
  const { getPgeStoreDownloadUrl } = useFileUploadApi();

  const lineItemRebateList =
    data?.getJob &&
    (data?.getJob.job.quote?.data?.lineItems?.filter(
      (item: any) => item.__typename === 'LineItemRebate',
    ) as LineItemRebate[]);

  const {
    totalCost,
    totalInstallation,
    costBeforeRebate,
    calculateTotalAmount,
  } = useQuote(content);

  const hr = {
    border: '1px solid #D7D7D7',
    margin: 0,
  };
  const totalInstallationCss = {
    display: 'flex',
    justifyContent: 'space-between',
  };

  useEffect(() => {
    if (data?.getJob) {
      const lineItemCostList = data?.getJob.job.quote?.data?.lineItems?.filter(
        (item: any) => item.__typename === 'LineItemCost',
      ) as LineItemCost[];
      const lineItemProductList = data?.getJob.job.quote?.data?.lineItems?.filter(
        (item: any) => item.__typename === 'LineItemProduct',
      ) as LineItemProduct[];
      calculateTotalAmount(false, {
        totalInstallationMaterial:
          (lineItemCostList &&
            lineItemCostList.filter(
              item => item.key === 'INSTALLATION_MATERIALS',
            )[0].amount) ||
          0,
        chargerCost:
          lineItemProductList &&
          lineItemProductList?.filter(
            item => item.key === 'EV_CHARGER_MODEL',
          )[0].amount,
        meterBase:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'METER_BASE')[0].amount,
        weatherHead:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'WEATHER_HEAD')[0]
            .amount,
        electricBreaker:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'ELECTRICAL_BREAKER')[0]
            .amount,
        miscMaterial:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'MISC_MATERIALS')[0]
            .amount,
        labor:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'LABOR')[0].amount,
        material:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'MATERIALS')[0].amount,
        permitCost:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'PERMITS')[0].amount,
        travelCost:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'TRAVEL_TIME')[0].amount,
        feesCost:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'FEES')[0].amount,
        miscellaneousWorkCost:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'MISCELLANEOUS_WORK')[0]
            .amount,
      });
    }
  }, [data?.getJob]);

  const downloadQuote = wrapWithLoader(
    async (quoteDocument: QuoteDocument) => {
      if (quoteDocument) {
        const files = await getPgeStoreDownloadUrl({
          keys: [quoteDocument.fileUrl],
        });

        const fileUrl = files[0].downloadUrl;

        const response = await fetch(fileUrl);
        const blob = await response.blob();

        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = quoteDocument.name || 'Invoice.pdf';

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        URL.revokeObjectURL(downloadLink.href);
      }
    },
    () => content.get('DOWNLOAD_QUOTE_ERROR_MSG') || '',
  );

  const getPageTitle = () => {
    if (
      data?.getJob?.job?.status === JobStatus.Canceled &&
      data?.getJob?.job?.quote?.status === QuoteStatus.Created
    ) {
      return content.get('SUBMITTED_QUOTE_HEADER');
    }
    if (data?.getJob?.job?.quote?.status === QuoteStatus.Rejected) {
      return content.get('REJECTED_QUOTE_TITLE');
    }

    return content.get('PGE_PLUS_SUBMITTED_QUOTE_TITLE');
  };

  return (
    <Layout>
      {loading && <Backdrop forceOpen />}
      {data && (
        <>
          <Grid
            container
            id="print_section"
            css={{
              marginTop: 16,
              marginBottom: 96,
              justifyContent: 'center',
            }}
          >
            <Grid item container xs={12} md={7} css={{ rowGap: 30 }}>
              <InstallerPageHeader
                pageHeaderInfo={{
                  pageTitle: getPageTitle(),
                }}
                cancelReasonInfo={
                  data?.getJob?.job?.status === JobStatus.Canceled &&
                  data?.getJob?.job?.quote.status === QuoteStatus.Created
                    ? {
                        reasonTitle: content.get(
                          'REASON_FOR_CANCELLATION_TITLE',
                        ),
                        statusReason: data?.getJob?.job?.statusReason ?? '',
                        downloadPackageTitle: content.get(
                          'REUSABLE_DOWNLOAD_LEAD_PACKAGE',
                        ),
                      }
                    : undefined
                }
                bannerText={inlineRichText(
                  content.get('QUOTE_SUCCESSFULLY_SENT_BANNER_TEXT'),
                  {
                    quoteSubmittedDate: format(
                      parse(
                        data?.getJob?.job?.quote?.updatedAt,
                        // eslint-disable-next-line @typescript-eslint/quotes
                        "yyyy-MM-dd'T'HH:mm:ss.SSSXXX",
                        new Date(),
                      ),
                      'MM/dd/yyyy',
                    ),
                  },
                )}
                content={content}
                jobData={data?.getJob}
                parentPage={'view-quote'}
              />
              <Grid item xs={12}>
                <QuoteCustomerInfo
                  content={content}
                  jobData={data?.getJob?.job?.jobLocation}
                  applicationNo={data?.getJob?.application?.applicationNo}
                />
              </Grid>
              <Grid item xs={12}>
                <hr css={{ ...hr }} />
              </Grid>
              <Grid item xs={12}>
                <JobInstallationCostDisplay
                  content={content}
                  jobData={data?.getJob?.job?.quote}
                  applicationData={data?.getJob?.application}
                  totalInstallationCost={totalInstallation}
                />
              </Grid>
              <Grid item xs={12}>
                <hr css={{ ...hr }} />
              </Grid>
              <Grid item xs={12}>
                <Box css={{ ...totalInstallationCss }}>
                  <Typography css={{ ...subTextWithoutMargin }}>
                    {content.get('REUSABLE_COST_BEFORE_REBATES')}
                  </Typography>
                  <Typography css={{ ...subTextWithoutMargin }}>
                    ${toCurrencyString(costBeforeRebate, true)}
                  </Typography>
                </Box>
                <Box css={{ ...totalInstallationCss }}>
                  <Typography css={{ ...subTextWithoutMargin }}>
                    {content.get('REUSABLE_REBATE_AMOUNT')}
                  </Typography>
                  <Typography css={{ ...subTextWithoutMargin }}>
                    $
                    {lineItemRebateList && lineItemRebateList[0]?.amount
                      ? toCurrencyString(lineItemRebateList[0]?.amount, true)
                      : '0.00'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <hr css={{ ...hr }} />
              </Grid>
              <Grid item xs={12}>
                <Box css={{ ...totalInstallationCss }}>
                  <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
                    {content.get('REUSABLE_TOTAL_COST')}
                  </Typography>
                  <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
                    $
                    {toCurrencyString(
                      Number(totalCost) -
                        (Number(
                          lineItemRebateList && lineItemRebateList[0]?.amount,
                        ) || 0),
                      true,
                    )}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <hr css={{ ...hr }} />
              </Grid>
              {data?.getJob?.job?.quote?.data?.quoteDocuments?.length > 0 && (
                <Grid item xs={12}>
                  <Typography
                    css={{
                      fontFamily: 'Forma-DJR-Display',
                      fontSize: '20px',
                      marginBottom: theme.spacing(2),
                    }}
                  >
                    {content.get('UPLOADED_QUOTE_DOCUMENTS')}
                  </Typography>
                  {data.getJob.job.quote.data.quoteDocuments?.map(
                    (quoteDocument: QuoteDocument, idx) => {
                      return (
                        <Box
                          css={{
                            margin: '16px 0',
                            padding: '12px',
                            border: '1px solid #f0f0f0',
                            borderRadius: '6px',
                            backgroundColor: colors.white,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                          key={idx}
                          onClick={() => downloadQuote(quoteDocument)}
                        >
                          <Typography
                            css={{
                              cursor: 'pointer',
                              color: colors.sparkBlue,
                              fontWeight: 'bold',
                            }}
                          >
                            {quoteDocument.name}
                          </Typography>
                          <SaveAltIcon
                            style={{
                              color: colors.sparkBlue,
                              marginRight: '4px',
                            }}
                          />
                        </Box>
                      );
                    },
                  )}
                </Grid>
              )}
              <Grid item xs={12} data-noprint>
                <Box
                  mt={5}
                  css={{
                    display: 'flex',
                    justifyContent: 'center',
                    [theme.breakpoints.down('sm')]: {
                      alignItems: 'center',
                      height: '96px',
                      backgroundColor: colors.cloudGrey,
                      position: 'fixed',
                      bottom: 0,
                      width: '100%',
                      left: 0,
                      boxShadow: `0px -3px 6px ${colors.boxShadowColor}`,
                    },
                  }}
                >
                  <InstallerButton
                    color={'primary'}
                    variant={'contained'}
                    onClick={() =>
                      void navigate(
                        ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_LISTING,
                      )
                    }
                  >
                    {content.get('REUSABLE_BACK_TO_HOME')}
                  </InstallerButton>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </Layout>
  );
};

export default ViewQuote;
